import Auth from '@aws-amplify/auth';
import { AUTH_LOGOUT } from './constants';
import Logger from 'utils/logger';
import api from 'common/api';
import { ssoAuthService } from 'features/auth/ssoAuthService';
import {
  SESSION_EXPIRED_STORAGE_KEY,
  SESSION_EXPIRED_RETURN_TO_KEY,
} from 'features/auth/constants';
import { redirectToLoginStartPage } from 'utils/path';

const LOGOUT_TIMEOUT_MS = 800;

const logoutMiddleware = (store) => (next) => (action) => {
  if (action && action.type === AUTH_LOGOUT) {
    const reason = action.meta && action.meta.reason;
    const isSessionExpired = reason === 'sessionExpired';
    const state = store.getState();
    const user = state && state.auth && state.auth.user;
    const isSsoUser = user && user.authMethod === 'sso';
    // eslint-disable-next-line no-undef
    const unifiedCookieEnabled = process.env.REACT_APP_UNIFIED_COOKIE_ENABLED === 'true';
    (async () => {
      try {
        if (!isSessionExpired) {
          const logoutPromises = [];

          if (isSsoUser) {
            // SSO users: clear tv_session cookie + Redis session
            logoutPromises.push(
              ssoAuthService.logout().catch((e) => {
                Logger.WARN && Logger.WARN('SSO session logout failed (continuing):', e);
              }),
            );
          } else {
            // Password users: call Cognito logout
            logoutPromises.push(
              api.post('/auth/logout', {}, { keepalive: true }).catch((e) => {
                Logger.WARN && Logger.WARN('Server logout failed (continuing):', e);
              }),
            );
            // Also clear tv_session cookie + Redis session if unified cookie is enabled,
            // because password users may have a tv_session from SessionExchange.
            if (unifiedCookieEnabled) {
              logoutPromises.push(
                ssoAuthService.logout().catch((e) => {
                  Logger.WARN && Logger.WARN('Session cookie logout failed (continuing):', e);
                }),
              );
            }
          }

          // Wait for logout to complete, but don't block more than timeout
          await Promise.race([
            Promise.all(logoutPromises),
            new Promise((resolve) => setTimeout(resolve, LOGOUT_TIMEOUT_MS)),
          ]);
        }
      } catch (e) {
        Logger.ERROR(e, 'Unexpected error during server logout');
      }
      // Sign out of Cognito first (before redirect), skip for SSO users
      try {
        if (!isSsoUser) {
          await Auth.signOut();
        }
      } catch (e) {
        Logger.ERROR(e, 'Error during Auth signOut');
      }
      // Now handle redirect/session-expired
      if (isSessionExpired) {
        try {
          if (typeof window !== 'undefined' && window.sessionStorage) {
            const { sessionStorage, location } = window;
            sessionStorage.setItem(SESSION_EXPIRED_STORAGE_KEY, '1');
            sessionStorage.setItem(
              SESSION_EXPIRED_RETURN_TO_KEY,
              `${location.pathname}${location.search}${location.hash}`,
            );
          }
        } catch (e) {
          Logger.WARN && Logger.WARN('Unable to persist session expired flag', e);
        }
      } else {
        try {
          if (typeof window !== 'undefined' && window.sessionStorage) {
            window.sessionStorage.removeItem(SESSION_EXPIRED_RETURN_TO_KEY);
          }
          redirectToLoginStartPage();
        } catch (e) {
          Logger.ERROR(e, 'Error during redirect to login page');
        }
      }
    })();
  }
  return next(action);
};

export default logoutMiddleware;
